import * as PopoverPrimitive from '@radix-ui/react-popover';

import { cn } from '@/lib/utils';

const BASE_CLASS = 'mm-floating-window';

export type FloatingWindowProps = React.ComponentProps<
  typeof PopoverPrimitive.Root
>;
function FloatingWindow({ ...props }: FloatingWindowProps) {
  return <PopoverPrimitive.Root data-slot="floating-window" {...props} />;
}

function FloatingWindowTrigger({
  className,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
  return (
    <PopoverPrimitive.Trigger
      data-slot="floating-window-trigger"
      className={cn('fixed bottom-4 right-4', className)}
      {...props}
    />
  );
}

function FloatingWindowContent({
  className,
  align = 'end',
  side = 'top',
  sideOffset = 8,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content>) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        data-slot="floating-window-content"
        align={align}
        side={side}
        sideOffset={sideOffset}
        className={cn(
          `${BASE_CLASS} z-50 w-72 rounded-md border bg-popover text-popover-foreground shadow-sm outline-none flex flex-col gap-6 py-6 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 brutalist:rounded-xs`,
          className
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
}

function FloatingWindowHeader({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="floating-window-header"
      className={cn(
        '@container/floating-window-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 has-data-[slot=floating-window-action]:grid-cols-[1fr_auto] [.border-b]:pb-6',
        className
      )}
      {...props}
    />
  );
}

function FloatingWindowTitle({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="floating-window-title"
      className={cn('leading-none font-semibold', className)}
      {...props}
    />
  );
}

function FloatingWindowDescription({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="floating-window-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
}

function FloatingWindowAction({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="floating-window-action"
      className={cn(
        'col-start-2 row-span-2 row-start-1 self-start justify-self-end',
        className
      )}
      {...props}
    />
  );
}

function FloatingWindowBody({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="floating-window-body"
      className={cn('px-6', className)}
      {...props}
    />
  );
}

function FloatingWindowFooter({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="floating-window-footer"
      className={cn('flex items-center px-6 [.border-t]:pt-6', className)}
      {...props}
    />
  );
}

function FloatingWindowClose(
  props: React.ComponentProps<typeof PopoverPrimitive.Close>
) {
  return (
    <PopoverPrimitive.Close data-slot="floating-window-close" {...props} />
  );
}

export {
  FloatingWindow,
  FloatingWindowTrigger,
  FloatingWindowContent,
  FloatingWindowTitle,
  FloatingWindowDescription,
  FloatingWindowHeader,
  FloatingWindowAction,
  FloatingWindowBody,
  FloatingWindowFooter,
  FloatingWindowClose,
};
